import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Shell from "./Shell";
import { useAdminStyles, num } from "./styles";

// Custo de AWS por serviço e por cliente.
//
// É ESTIMATIVA, e a tela diz isso em letras: sai do nosso metering multiplicado
// por preço de tabela, não da fatura. A fatura vem por serviço e por conta, sem
// saber de cliente nenhum — e "quanto este cliente custa" é justamente a
// pergunta que se faz aqui.

// abaixo de um centavo o valor em reais vira 0,00 e a tabela parece quebrada
const dinheiro = cents => {
  if (!cents) return "—";
  if (cents < 1) return `${cents.toFixed(4)} centavos`;
  return `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
};

const JANELAS = [7, 30, 90];

const AwsCosts = () => {
  const classes = useAdminStyles();
  const history = useHistory();
  const [d, setD] = useState(null);
  const [dias, setDias] = useState(30);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/aws-costs", { params: { dias } });
      setD(data);
    } catch (err) {
      toastError(err);
    }
  }, [dias]);

  useEffect(() => { load(); }, [load]);

  if (!d) return <Shell title="Custos AWS"><div className={classes.empty}>Carregando…</div></Shell>;

  const { servicos, porCliente, precos, totalCents } = d;
  const atribuiveis = servicos.filter(s => s.atribuivel);
  const fora = servicos.filter(s => !s.atribuivel);
  const maior = atribuiveis.reduce((m, s) => Math.max(m, s.custoCents), 0) || 1;

  return (
    <Shell title="Custos AWS">
      <div className={classes.filters}>
        {JANELAS.map(j => (
          <div key={j} className={`${classes.chip} ${dias === j ? classes.chipOn : ""}`} onClick={() => setDias(j)}>
            {j} dias
          </div>
        ))}
        <div className={classes.grow} />
        <div className={classes.muted}>câmbio usado: US$ 1 = R$ {precos.usdBrl}</div>
      </div>

      <div className={classes.cards}>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Total estimado</div>
          <div className={classes.kpiValue}>{dinheiro(totalCents)}</div>
          <div className={classes.kpiHint}>últimos {d.dias} dias</div>
        </div>
        <div className={classes.kpi}>
          <div className={classes.kpiLabel}>Clientes com custo</div>
          <div className={classes.kpiValue}>{num(porCliente.length)}</div>
          <div className={classes.kpiHint}>geraram consumo no período</div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Por serviço</div>
        <div className={classes.cardSub}>
          Estimativa a partir do nosso próprio consumo medido × preço de tabela. <b>Não é a fatura da AWS</b> —
          a fatura vem por conta, sem separar cliente.
        </div>
        {atribuiveis.map((s, i) => (
          <div key={`${s.servico}-${i}`} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div style={{ width: 96 }} className={classes.strong}>{s.servico}</div>
            <div className={classes.grow}>
              <div className={classes.muted}>{s.detalhe}</div>
              <div className={classes.bar}>
                <div className={classes.barFill} style={{ width: `${(s.custoCents / maior) * 100}%` }} />
              </div>
            </div>
            <div className={classes.muted} style={{ width: 150, textAlign: "right" }}>
              {num(s.quantidade)} {s.unidade}
            </div>
            <div className={classes.num} style={{ width: 130, textAlign: "right" }}>{dinheiro(s.custoCents)}</div>
          </div>
        ))}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Fora da estimativa</div>
        <div className={classes.cardSub}>
          Custos reais que existem na fatura mas não sabemos atribuir a cliente — e por isso aparecem zerados aqui.
        </div>
        {fora.map((s, i) => (
          <div key={`f${i}`} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div style={{ width: 96 }} className={classes.strong}>{s.servico}</div>
            <div className={classes.grow}><div className={classes.muted}>{s.detalhe}</div></div>
          </div>
        ))}
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Por cliente</div>
        <div className={classes.cardSub}>Quem gerou o consumo. Clique para abrir a ficha.</div>
        {porCliente.length === 0 && <div className={classes.empty}>Nenhum consumo de AWS no período.</div>}
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                {["Cliente", "E-mails", "Execuções", "Tempo Lambda", "Armazenado", "Voz Bedrock", "Custo"].map((h, i) => (
                  <th key={h} className={`${classes.th} ${classes.thPlain}`} style={i ? { textAlign: "right" } : undefined}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {porCliente.map(c => (
                <tr key={c.userId} className={classes.trLink} onClick={() => history.push(`/admin/users/${c.userId}`)}>
                  <td className={classes.td}>
                    <div className={classes.strong}>{c.name || "—"}</div>
                    <div className={classes.muted}>{c.email}</div>
                  </td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(c.emails)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{num(c.execucoes)}</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{c.lambdaSegundos}s</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{c.armazenamentoMb} MB</td>
                  <td className={`${classes.td} ${classes.tdNum}`}>{c.vozBedrockMin} min</td>
                  <td className={`${classes.td} ${classes.tdNum}`}><b>{dinheiro(c.custoCents)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>Preços usados</div>
        <div className={classes.cardSub}>
          Preço de tabela, configurável por variável de ambiente. Se um número parecer errado, é aqui que se corrige.
        </div>
        {[
          ["SES — por e-mail", `US$ ${precos.sesPerEmailUsd}`, "AWS_SES_PER_EMAIL_USD"],
          ["Lambda — por invocação", `US$ ${precos.lambdaPerRequestUsd}`, "AWS_LAMBDA_PER_REQUEST_USD"],
          ["Lambda — por GB-segundo", `US$ ${precos.lambdaPerGbSecondUsd}`, "AWS_LAMBDA_PER_GB_SECOND_USD"],
          ["Lambda — memória da função", `${precos.lambdaMemoryMb} MB`, "AWS_LAMBDA_MEMORY_MB"],
          ["S3 — GB por mês", `US$ ${precos.s3StorageGbMonthUsd}`, "AWS_S3_STORAGE_GB_MONTH_USD"],
          ["S3 — PUT por mil", `US$ ${precos.s3PutPer1kUsd}`, "AWS_S3_PUT_PER_1K_USD"],
          ["S3 — saída por GB", `US$ ${precos.s3EgressGbUsd}`, "AWS_S3_EGRESS_GB_USD"],
          ["Bedrock — minuto de voz (estimado)", precos.bedrockVoiceMinuteUsd ? `US$ ${precos.bedrockVoiceMinuteUsd}` : "não configurado", "AWS_BEDROCK_VOICE_MINUTE_USD"],
          ["CloudWatch — GB de log", `US$ ${precos.cloudwatchGbUsd}`, "AWS_CLOUDWATCH_GB_USD"],
          ["Câmbio", `R$ ${precos.usdBrl}`, "AWS_USD_BRL"],
        ].map(([label, valor, env], i) => (
          <div key={env} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
            <div className={classes.grow}>
              <div className={classes.strong}>{label}</div>
              <div className={classes.muted} style={{ fontFamily: "monospace", fontSize: 11.5 }}>{env}</div>
            </div>
            <div className={classes.num}>{valor}</div>
          </div>
        ))}
      </div>
    </Shell>
  );
};

export default AwsCosts;
